import { useMemo } from 'react';
import type { HistoryResponse, LeagueBundle } from '../../shared/types';
import { Avatar, ErrorState, Legend, Loading, PageHead, Stat } from '../components/common';
import { useIncidents, useScores } from '../lib/api';
import { fmtPct, fmtPoints } from '../lib/format';
import {
  buildProfiles,
  careerDifferential,
  careerEfficiency,
  careerPct,
  careerPpg,
  careerTotals,
  recordText,
  type CareerRecord,
  type Honour,
  type ManagerProfile,
} from '../lib/managers';
import { managerDirectory } from '../lib/rivalries';
import { Link } from '../lib/router';
import type { Appearance } from '../lib/records';

/**
 * "High score" and "Low score", NOT "Best game" and "Worst game".
 *
 * The tiles below already carry those two labels, and they mean something
 * different: a tile is this manager's best game ever, while the badge is the
 * whole league's best game that season. Reusing the wording would put the two
 * claims side by side under one name, and for a manager holding both they
 * would read as one fact stated twice. These also match the record book's own
 * board titles, so /records and /managers name the same thing the same way.
 */
const HONOUR_LABEL: Record<Honour['kind'], string> = {
  champion: 'Champion',
  'runner-up': 'Runner-up',
  third: 'Third',
  'high-score': 'High score',
  // "Most efficient", not "Best lineup": the second reads as one week's lineup,
  // which is a claim this badge does not make -- it is a whole regular season's
  // share of the optimal. The record book calls the same figure "Best Lineup
  // Efficiency", and this is the short form of that rather than a new name.
  efficiency: 'Most efficient',
  'worst-record': 'Worst record',
  'low-score': 'Low score',
  // Names the manager's side of it, not the players'. "Most suspensions" would
  // read as a claim about people who are not on this page and cannot answer
  // it; what the badge actually ranks is how many of a roster's players were
  // unavailable for a reason that was not an injury.
  'off-field': 'Off-field absences',
};

/**
 * The mark each badge prints in place of its label.
 *
 * The words above are NOT gone -- they are the `aria-label` and the tooltip,
 * and the legend still spells every one of them out. That split is the whole
 * reason this is safe: an emoji is a mnemonic once you know the claim and a
 * guess before that, so the claim has to remain findable somewhere. Dropping
 * the text outright would leave a card asserting things in pictures with no
 * key, which is the mirror of the glossary-defines-a-term-nobody-can-find
 * defect the tiles already had to fix.
 *
 * `🔥` / `🧊` pair the two single-game extremes as hot and cold rather than
 * giving the low score a second sad face to argue with the wooden spoon.
 *
 * **`🇱` is a LONE regional indicator, and that is load-bearing.** It renders
 * as a boxed letter — the "taking the L" idiom — only while it stands by
 * itself: two regional indicators in sequence are a country flag, so `🇱`
 * followed by another one would silently become a flag of somewhere. Nothing
 * here can produce that today (a badge holds exactly one mark, and no other
 * mark in this map is a regional indicator), but a second one added later
 * would break this one from a distance, in a file that never mentions it. If
 * a mark ever needs to sit beside `🇱` in a string — a legend term, say —
 * separate them with something that is not a regional indicator.
 */
const HONOUR_MARK: Record<Honour['kind'], string> = {
  champion: '🏆',
  'runner-up': '🥈',
  third: '🥉',
  'high-score': '🔥',
  efficiency: '🎯',
  'worst-record': '🇱',
  'low-score': '🧊',
  // A plain colour emoji, chosen after the `🇱` lesson: an emoji whose
  // appearance matters has to be rendered and MEASURED on the target platform,
  // because Chrome on Windows draws a lone regional indicator as a 5.8px bare
  // letter where Apple boxes it. This is not a regional indicator, so it also
  // cannot combine with `🇱` into a country flag.
  'off-field': '🚨',
};

function Badge({ honour }: { honour: Honour }) {
  const label = HONOUR_LABEL[honour.kind];
  return (
    <span className={`honour honour-${honour.kind}`} title={label}>
      <span className="honour-year">{honour.season}</span>
      <span className="honour-mark" role="img" aria-label={label}>
        {HONOUR_MARK[honour.kind]}
      </span>
    </span>
  );
}

/**
 * A single-game tile.
 *
 * Null before a manager's first game rather than 0.00 -- a team that has not
 * played has no low score, and printing one would put a brand-new manager at
 * the bottom of a page nobody would think to distrust.
 */
function GameStat({
  label,
  game,
  name,
}: {
  label: string;
  game: Appearance | null;
  name: (id: string) => string;
}) {
  if (!game) return <Stat label={label} value="—" note="No games yet" />;
  return (
    <Stat
      label={label}
      value={fmtPoints(game.points)}
      note={`${game.season} wk ${game.week} · vs\u00a0${name(game.opponentId)}`}
    />
  );
}

/**
 * A signed points total.
 *
 * Zero takes no sign at all rather than a `+`. The value stays UNCOLOURED, like
 * every other tile on this card: the sign already carries the direction, and
 * tinting one value in a row of seven makes it read as the one that matters.
 * `/standings` does colour its differential column, which is a different job --
 * there it is a comparison between rows, here it is a fact about one manager.
 * (That column also paints an exact 0.00 green, which is the same trap as the
 * green em-dash this repo already shipped once.)
 */
const fmtDiff = (n: number): string => `${n > 0 ? '+' : ''}${fmtPoints(n)}`;

function ManagerCard({
  profile,
  name,
}: {
  profile: ManagerProfile;
  name: (id: string) => string;
}) {
  const { regular, playoff, unclassified } = profile;
  const span =
    profile.seasons.length > 1
      ? `${profile.seasons[profile.seasons.length - 1]}–${profile.seasons[0]}`
      : profile.seasons[0];

  const rec = (c: CareerRecord) => recordText(c.wins, c.losses, c.ties);
  const pct = careerPct(regular);
  const ppg = careerPpg(regular);
  const all = careerTotals(profile);
  const diff = careerDifferential(profile);
  const eff = careerEfficiency(profile);

  const title = <span className="mgr-name">{profile.teamName}</span>;

  return (
    <article className="card card-pad mgr-card">
      <header className="mgr-head">
        <Avatar id={profile.user.avatar} alt={profile.teamName} large />
        <div className="mgr-id">
          {profile.rosterId != null ? <Link to={`/teams/${profile.rosterId}`}>{title}</Link> : title}
          <div className="team-handle">
            @{profile.user.name} · {span}
            {!profile.user.active && <span className="pill tiny">Former</span>}
          </div>
        </div>
        {profile.honours.length > 0 && (
          <div className="mgr-honours">
            {profile.honours.map((h) => (
              <Badge key={`${h.season}-${h.kind}`} honour={h} />
            ))}
          </div>
        )}
      </header>

      <div className="grid grid-4 mgr-tiles">
        <Stat
          label="Regular season"
          value={regular.games === 0 ? '—' : rec(regular)}
          note={
            pct == null || ppg == null
              ? 'No games yet'
              : `${fmtPct(pct)} · ${fmtPoints(ppg)}\u00a0per\u00a0game`
          }
        />
        <Stat
          label="Playoffs"
          value={playoff.games === 0 ? '—' : rec(playoff)}
          note={
            profile.decidedSeasons === 0
              ? 'No season decided yet'
              : profile.playoffAppearances === 0
                ? 'Never qualified'
                : `${profile.playoffAppearances} of ${profile.decidedSeasons} season${
                    profile.decidedSeasons === 1 ? '' : 's'
                  }`
          }
        />
        {/* Every game, playoffs and consolation included -- which is a WIDER
            span than the Regular season tile two along and than the efficiency
            tile beside it, so the note names it. Two plausible career totals
            under one card with neither labelled is exactly how /records and
            /managers came to disagree about 11-5 versus 11-3. */}
        {/* One word each, and that is a LAYOUT constraint rather than a
            preference. `.stat-label` wraps, and a label that takes two lines
            pushes its value down 16px while its rowmates stay put -- measured
            at 1180px, where "Point differential" wrapped and "Lineup
            efficiency" did not, so one value in the row sat lower than the
            other two. Shortening only one of them just moves the problem to
            390px, where the tracks are 158px and "Lineup efficiency" is the
            one that wraps. Both have to be short for the row to align at both
            ends, so resist expanding either back: the notes below carry the
            precision the labels gave up. */}
        <Stat
          label="Differential"
          value={diff == null ? '—' : fmtDiff(diff)}
          note={
            diff == null
              ? 'No games yet'
              : `${fmtPoints(all.pointsFor)} for · ${all.games} game${
                  all.games === 1 ? '' : 's'
                }, all rounds`
          }
        />
        {/* Pooled across seasons rather than averaged per season, and regular
            season only because `fpts`/`ppts` are. It is also the figure the 🎯
            badge withholds -- the badge names a season's leader and prints no
            number, on the understanding that the number is on a tile nearby,
            and until now this was the one badge for which that was untrue. */}
        <Stat
          label="Efficiency"
          value={eff == null ? '—' : fmtPct(eff)}
          note={
            eff == null
              ? 'No games yet'
              : `${fmtPoints(profile.lineupFor)} of ${fmtPoints(
                  profile.lineupPotential,
                )} · regular season`
          }
        />
        {/* Only reachable for a season whose playoffs were never seeded, which
            is why it is conditional rather than a permanent column of dashes. */}
        {unclassified.games > 0 && (
          <Stat
            label="Playoff weeks"
            value={rec(unclassified)}
            note="Not in either bracket"
          />
        )}
        <GameStat label="Best game" game={profile.best} name={name} />
        <GameStat label="Worst game" game={profile.worst} name={name} />
        {/* Withheld while a manager has only one ranked season, because then it
            IS the career line two tiles to the left -- the card would print
            "2-12" twice under different labels and claim to have said two
            things. It appears on its own the year a second season lands. */}
        {profile.bestSeason && profile.scoredSeasons > 1 && (
          <Stat
            label="Best regular season"
            value={recordText(
              profile.bestSeason.wins,
              profile.bestSeason.losses,
              profile.bestSeason.ties,
            )}
            note={`${profile.bestSeason.season} · ${fmtPoints(profile.bestSeason.points)} pts`}
          />
        )}
      </div>
    </article>
  );
}

export default function Managers({
  bundle,
  history,
}: {
  bundle: LeagueBundle;
  history: HistoryResponse | null;
}) {
  // Same fan-out as Records and Rivalries. `shared()` dedupes the requests, so
  // arriving here after either of those costs nothing.
  const leagueIds = useMemo(() => {
    const ids = [bundle.league.league_id];
    for (const s of history?.seasons ?? []) ids.push(s.league_id);
    return ids;
  }, [bundle.league.league_id, history]);

  const { data, error, loading, reload } = useScores(leagueIds);
  // Not gated on: the badge it feeds is one of eight, and a page that refused
  // to render the other seven because a 1 KB static file was slow would be
  // trading everything for the least of it. A null ledger simply awards no
  // off-field badge, which is also what an empty one does.
  const incidents = useIncidents();
  const dir = useMemo(() => managerDirectory(bundle, history), [bundle, history]);
  const profiles = useMemo(
    () => buildProfiles(bundle, history, data ?? [], incidents.data),
    [bundle, history, data, incidents.data],
  );

  if (loading) return <Loading label="Loading every game ever played" />;
  if (error || !data) {
    return <ErrorState message={error ?? 'No score data returned'} onRetry={reload} />;
  }

  const name = (id: string) => dir.get(id)?.name ?? 'Unknown';
  const seasons = new Set(profiles.flatMap((p) => p.seasons)).size;
  const former = profiles.filter((p) => !p.user.active).length;
  // A glossary entry for a tile no card is showing defines a term the reader
  // cannot find, which is the mirror of the empty column the schedule page
  // already had to fix, so this definition is conditional like its tile.
  const anyBestSeason = profiles.some((p) => p.bestSeason && p.scoredSeasons > 1);

  return (
    <>
      <PageHead
        title="Managers"
        meta={[
          `${profiles.length} managers`,
          seasons === 1 ? 'one season' : `${seasons} seasons`,
          former > 0 && `${former} no longer in the league`,
          'Ranked by titles',
        ]}
      />

      <div className="mgr-list">
        {profiles.map((p) => (
          <ManagerCard key={p.user.user_id} profile={p} name={name} />
        ))}
      </div>

      <Legend
        items={[
          { term: 'Playoffs', def: 'Winners-bracket games only' },
          // The badges print a mark rather than their claim, so this is now the
          // key to them and not merely a glossary. Each term LEADS with the mark
          // and still names the thing in words -- a reader matching a card to
          // this list needs the picture, and one arriving at the list first
          // needs the words. `dt` is `white-space: nowrap` inside a 270px track,
          // so the podium three share one entry rather than eating three of
          // them; the four that nobody could guess get their own.
          { term: '🏆 🥈 🥉', def: 'Champion, runner-up and third that season' },
          {
            term: '🔥 / 🧊',
            def: 'High and low score — the league’s biggest and smallest single-game totals that season, any week',
          },
          // One entry for the tile and its badge together. They are the same
          // quantity measured over different spans -- the tile is a career
          // figure, the mark names one season's leader -- so defining them
          // separately would print the same sentence twice and still leave the
          // relationship between them unstated.
          {
            term: 'Efficiency 🎯',
            def: 'Points scored as a share of the best lineup available, regular season only — the mark is that season’s leader',
          },
          {
            term: 'Differential',
            def: 'Points for minus points against, every postseason game included',
          },
          { term: '🇱', def: 'Worst record — last in that season’s regular-season table' },
          // Names the window on its face. Every other entry here describes a
          // fact the league's own data holds forever; this one is bounded by
          // the night the observation log started, and a reader has no way to
          // guess that from a badge.
          {
            term: '🚨',
            def: 'Most players suspended, holding out or away for personal reasons that season — a mix of nightly detection and incidents entered by hand, so it is a record rather than a sweep',
          },
          anyBestSeason && {
            term: 'Best regular season',
            def: 'Weeks before the bracket, so playoff wins are not in it',
          },
        ]}
      />
    </>
  );
}
